import React, { useState, useEffect } from 'react';
import { io } from 'socket.io-client';

/**
 * AnnouncementBanner — Shows a pinned site-wide message below the nav.
 * Fetches from /api/announcement on mount, listens for real-time updates via Socket.IO.
 * Supports types: info (blue), warn (amber), success (green).
 */
export default function AnnouncementBanner() {
  const [announcement, setAnnouncement] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Fetch current announcement
    fetch('/api/announcement')
      .then(r => r.json())
      .then(data => {
        if (data && data.message) setAnnouncement(data);
      })
      .catch(() => {});

    // Listen for real-time updates
    const socket = io(window.location.hostname === 'localhost'
      ? 'http://localhost:3001'
      : window.location.origin
    );
    socket.on('announcement_updated', (data) => {
      setAnnouncement(data);
      setDismissed(false); // Show again on update
    });

    return () => socket.disconnect();
  }, []);

  if (!announcement || !announcement.message || dismissed) return null;

  const styles = {
    info: {
      bg: 'linear-gradient(135deg, rgba(59,130,246,0.08), rgba(59,130,246,0.03))',
      border: 'rgba(59,130,246,0.15)',
      text: 'text-blue-300/90',
      icon: 'ℹ️',
      dot: 'bg-blue-400',
    },
    warn: {
      bg: 'linear-gradient(135deg, rgba(245,158,11,0.08), rgba(245,158,11,0.03))',
      border: 'rgba(245,158,11,0.15)',
      text: 'text-amber-300/90',
      icon: '⚠️',
      dot: 'bg-amber-400',
    },
    success: {
      bg: 'linear-gradient(135deg, rgba(34,197,94,0.08), rgba(34,197,94,0.03))',
      border: 'rgba(34,197,94,0.15)',
      text: 'text-emerald-300/90',
      icon: '🎉',
      dot: 'bg-emerald-400',
    },
  };

  const s = styles[announcement.type] || styles.info;

  return (
    <div
      className="relative z-40"
      style={{
        background: s.bg,
        borderBottom: `1px solid ${s.border}`,
      }}
    >
      <div className="max-w-5xl mx-auto px-4 md:px-6 py-2 flex items-center gap-3">
        <span className={`w-1.5 h-1.5 rounded-full ${s.dot} flex-shrink-0`} />
        <p className={`text-xs font-mono ${s.text} flex-1`}>
          {announcement.message}
          {announcement.link && (
            <a
              href={announcement.link}
              target="_blank"
              rel="noopener noreferrer"
              className="ml-2 underline underline-offset-2 hover:opacity-80 transition-opacity"
            >
              Learn more →
            </a>
          )}
        </p>
        <button
          onClick={() => setDismissed(true)}
          className="text-white/20 hover:text-white/50 transition-colors cursor-pointer flex-shrink-0"
          aria-label="Dismiss announcement"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
}
